import { filterByAppType, useQueryGetAllBots } from "@apis:bot/bot";
import { LoadingScreen } from "@components:LoadingScreen";
import { NoBots } from "@components:NoBots";
import { useRBACCheck } from "@hooks:useRBAC";
import { Navigate } from "react-router";

import { AppType } from "@:constants";
import { FeatureFlag } from "@:flag";
import { RBACAction, RBACResource } from "@:rbac";

/**
 * Smart redirect component that determines the best available route
 * Priority: chat → docs → audio → translation → analytics → "no bots" page
 * Shows loading screen during bot fetch
 * Respects feature flags and RBAC permissions
 */
export const SmartRedirect = () => {
  const { data: allBots, isLoading } = useQueryGetAllBots(true);
  const canAccessAnalytics = useRBACCheck(
    RBACAction.read,
    RBACResource.analytics,
  );

  // Show loading during fetch
  if (isLoading) {
    return <LoadingScreen />;
  }

  // No bots available
  if (!allBots || allBots.length === 0) {
    return <NoBots />;
  }

  // Priority 1: Chat/Search (most common)
  if (filterByAppType(allBots, AppType.Search).length > 0) {
    return <Navigate to="/app/chat" replace />;
  }

  // Priority 2: Docs
  if (FeatureFlag.Docs && filterByAppType(allBots, AppType.Docs).length > 0) {
    return <Navigate to="/app/docs" replace />;
  }

  // Priority 3: Audio
  if (FeatureFlag.Audio && filterByAppType(allBots, AppType.Audio).length > 0) {
    return <Navigate to="/app/audio" replace />;
  }

  // Priority 4: Translation
  if (
    FeatureFlag.Translation &&
    filterByAppType(allBots, AppType.Translation).length > 0
  ) {
    return <Navigate to="/app/document-translation" replace />;
  }

  // Priority 5: Analytics (admin only)
  if (FeatureFlag.Analytics && canAccessAnalytics) {
    return <Navigate to="/app/analytics" replace />;
  }

  // No bot usable by any enabled app
  return <NoBots />;
};
